import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useJoinWithCodeMutation, useValidateInviteCodeQuery, useGetCompanyPositionsQuery } from "../../services/api";
import { setCredentials } from "../../store/slices/authSlice";
import ErrorMessage from "../../Components/common/ErrorMessage";
import Loading from "../../Components/common/Loading";
import "./JoinCompany.css";

const JoinCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [joinWithCode, { isLoading: isJoining, error: joinError }] = useJoinWithCodeMutation();

  const [inviteCode, setInviteCode] = useState(searchParams.get("code") || "");
  const [shouldValidate, setShouldValidate] = useState(false);

  // Validate invite code when user types a complete code
  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError,
  } = useValidateInviteCodeQuery(inviteCode, {
    skip: !shouldValidate || inviteCode.length < 9, // Format: ABCD-1234 (9 chars)
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });

  const [selectedPosition, setSelectedPosition] = useState("");
  const [validationErrorMsg, setValidationErrorMsg] = useState("");

  // Fetch positions when company is validated
  const companyId = validationData?.company?._id;
  const { data: positions = [], isLoading: positionsLoading } = useGetCompanyPositionsQuery(
    companyId,
    { skip: !companyId }
  );

  useEffect(() => {
    // Auto-validate when invite code looks complete
    if (inviteCode.length >= 9) {
      setShouldValidate(true);
    } else {
      setShouldValidate(false);
    }
  }, [inviteCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "inviteCode") {
      // Format invite code as ABCD-1234
      let formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (formatted.length > 4) {
        formatted = formatted.slice(0, 4) + "-" + formatted.slice(4, 8);
      }
      setInviteCode(formatted);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear validation error when user starts typing
    if (validationErrorMsg) setValidationErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrorMsg("");

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setValidationErrorMsg("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setValidationErrorMsg("Password must be at least 8 characters");
      return;
    }

    if (!inviteCode || inviteCode.length < 9) {
      setValidationErrorMsg("Please enter a valid invite code");
      return;
    }

    try {
      const payload = {
        user: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
        },
        inviteCode: inviteCode.replace("-", ""), // Remove hyphen for API
        positionId: selectedPosition || undefined,
      };

      const response = await joinWithCode(payload).unwrap();

      // Auto-login after successful registration
      if (response.user && response.company && response.tokens) {
        dispatch(setCredentials(response));
        navigate("/onboarding");
      }
    } catch (err) {
      console.error("Join failed:", err);
      // Error handled by RTK Query
    }
  };

  return (
    <div className="join-company-page">
      <div className="join-company-container">
        <div className="join-company-card">
          <div className="join-company-header">
            <h1>Join a Company</h1>
            <p>Enter your invite code to get started</p>
          </div>

          {joinError && <ErrorMessage error={joinError} />}
          {validationErrorMsg && (
            <div className="validation-error">{validationErrorMsg}</div>
          )}

          <form onSubmit={handleSubmit} className="join-company-form">
            {/* Invite Code Section */}
            <div className="form-section invite-section">
              <div className="form-group">
                <label htmlFor="inviteCode">Invite Code *</label>
                <input
                  type="text"
                  id="inviteCode"
                  name="inviteCode"
                  value={inviteCode}
                  onChange={handleChange}
                  placeholder="ABCD-1234"
                  required
                  autoFocus
                  maxLength="9"
                  pattern="[A-Z0-9]{4}-[A-Z0-9]{4}"
                  title="Format: ABCD-1234"
                  className="invite-code-input"
                />
              </div>

              {/* Show company info when code is valid */}
              {isValidating && (
                <div className="code-validation">
                  <Loading />
                  <span>Validating code...</span>
                </div>
              )}

              {validationData?.company && (
                <div className="company-preview">
                  <div className="company-preview-icon">✓</div>
                  <div className="company-preview-info">
                    <h4>You're joining</h4>
                    <p className="company-name">{validationData.company.name}</p>
                    {validationData.company.description && (
                      <p className="company-desc">{validationData.company.description}</p>
                    )}
                  </div>
                </div>
              )}

              {validationError && inviteCode.length >= 9 && (
                <div className="code-error">
                  <span>❌</span>
                  <span>Invalid or expired invite code</span>
                </div>
              )}

              {/* Position Selection - Show when company is validated */}
              {validationData?.company && (
                <div className="position-select-wrapper">
                  <label htmlFor="position">Select Your Role</label>
                  {positionsLoading ? (
                    <div className="position-loading">Loading roles...</div>
                  ) : positions.length > 0 ? (
                    <select
                      id="position"
                      value={selectedPosition}
                      onChange={(e) => setSelectedPosition(e.target.value)}
                      className="position-select"
                    >
                      <option value="">-- Select your role (optional) --</option>
                      {positions.map((pos) => (
                        <option key={pos._id} value={pos._id}>
                          {pos.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="position-empty">No roles available yet</div>
                  )}
                </div>
              )}
            </div>

            {/* User Information Section */}
            <div className="form-section">
              <h3>Your Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="join-company-btn"
              disabled={isJoining || !validationData?.company}
            >
              {isJoining ? "Joining..." : "Join Company"}
            </button>
          </form>

          <div className="join-company-footer">
            <p>
              Don't have an invite code?{" "}
              <Link to="/create-company" className="create-link">
                Create your own company
              </Link>
            </p>
            <p>
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinCompany;
